import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';

const locations = [
  { 
    key: 'topanga', 
    icon: 'ri-landscape-line', 
    delay: 0,
    image: 'https://public.readdy.ai/ai/img_res/f514b48ca7285baae28b20fef37e21f1.jpg'
  },
  { 
    key: 'pasadena', 
    icon: 'ri-building-4-line', 
    delay: 100,
    image: 'https://readdy.ai/api/search-image?query=Pasadena%20California%20downtown%20real%20street%20view%2C%20Colorado%20Boulevard%2C%20historic%20buildings%2C%20City%20Hall%2C%20real%20urban%20architecture%2C%20palm%20trees%2C%20commercial%20district%2C%20realistic%20daytime%20photography&width=600&height=400&seq=pasadena-real-streets-2025&orientation=landscape'
  },
  { 
    key: 'santaana', 
    icon: 'ri-store-3-line', 
    delay: 200,
    image: 'https://readdy.ai/api/search-image?query=Santa%20Ana%20California%20downtown%20real%20street%20view%2C%20Fourth%20Street%2C%20historic%20buildings%2C%20urban%20commercial%20district%2C%20real%20architecture%2C%20palm%20tree%20lined%20streets%2C%20realistic%20daytime%20photography&width=600&height=400&seq=santaana-real-streets-2025&orientation=landscape'
  },
  { 
    key: 'riverside', 
    icon: 'ri-community-line', 
    delay: 300,
    image: 'https://readdy.ai/api/search-image?query=Riverside%20California%20downtown%20real%20street%20view%2C%20Mission%20Inn%20Avenue%2C%20historic%20buildings%2C%20urban%20landscape%2C%20real%20architecture%2C%20palm%20trees%2C%20commercial%20district%2C%20realistic%20daytime%20photography&width=600&height=400&seq=riverside-real-streets-2025&orientation=landscape'
  },
  { 
    key: 'torrance', 
    icon: 'ri-hotel-line', 
    delay: 400,
    image: 'https://readdy.ai/api/search-image?query=Torrance%20California%20real%20street%20view%2C%20South%20Bay%20urban%20landscape%2C%20commercial%20buildings%2C%20modern%20architecture%2C%20palm%20tree%20lined%20streets%2C%20residential%20and%20business%20district%2C%20realistic%20daytime%20photography&width=600&height=400&seq=torrance-real-streets-2025&orientation=landscape'
  },
  { 
    key: 'socal', 
    icon: 'ri-sun-line', 
    delay: 500,
    image: 'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Southern%20California%20coastline%20with%20real%20buildings%2C%20Pacific%20Coast%20Highway%2C%20palm%20tree%20lined%20streets%2C%20beach%20cities%2C%20urban%20landscape%2C%20modern%20architecture%2C%20sunny%20day%2C%20realistic%20photography&width=600&height=400&seq=socal-real-streets-2025&orientation=landscape'
  },
];

export default function ServiceAreas() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const isScrollingRef = useRef(false);

  const totalLocations = locations.length;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animate mobile cards when section becomes visible
  useEffect(() => {
    if (isVisible) {
      locations.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 150);
      });
    }
  }, [isVisible]);

  const handleManualScroll = (direction: 'left' | 'right') => {
    // Prevent multiple clicks while scrolling
    if (isScrollingRef.current) return;
    
    isScrollingRef.current = true;
    setIsPaused(true);
    
    // Clear existing timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    // Resume auto-scroll after 10 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 10000);

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 288 + 16; // card width + gap
      
      let newIndex = currentIndex;
      if (direction === 'left') {
        newIndex = currentIndex <= 0 ? totalLocations - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex >= totalLocations - 1 ? 0 : currentIndex + 1;
      }
      
      setCurrentIndex(newIndex);
      container.scrollTo({ left: newIndex * cardWidth, behavior: 'smooth' });
      
      // Allow next click after scroll animation completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500);
    } else {
      isScrollingRef.current = false;
    }
  };

  // Auto-scroll with infinite loop
  useEffect(() => {
    if (isPaused) return;
    
    const container = scrollContainerRef.current;
    if (!container) return;

    const autoScroll = setInterval(() => {
      const cardWidth = 288 + 16; // card width + gap
      let nextIndex = currentIndex + 1;
      
      if (nextIndex >= totalLocations) {
        nextIndex = 0;
      }
      
      setCurrentIndex(nextIndex);
      container.scrollTo({ left: nextIndex * cardWidth, behavior: 'smooth' });
    }, 5000);

    return () => clearInterval(autoScroll);
  }, [isPaused, currentIndex, totalLocations]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="projects"
      className="relative py-12 md:py-32 overflow-hidden bg-[#FAFBFC]"
    >
      {/* Subtle decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-10 md:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 md:px-4 md:py-2 bg-accent/10 text-accent text-xs md:text-sm font-medium rounded-full mb-3 md:mb-6">
            <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-accent rounded-full animate-pulse"></span>
            {t('serviceAreas.badge')}
          </span>

          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-3 md:mb-5 leading-tight">
            {t('serviceAreas.title')}
          </h2>

          <p className="text-gray-500 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t('serviceAreas.subtitle')}
          </p>
        </div>

        {/* Mobile: Horizontal scroll carousel with navigation arrows */}
        <div className="md:hidden relative">
          {/* Left Arrow */}
          <button 
            onClick={() => handleManualScroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
          >
            <i className="ri-arrow-left-s-line text-lg text-[#0F172A]"></i>
          </button>
          
          {/* Right Arrow */}
          <button 
            onClick={() => handleManualScroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
          >
            <i className="ri-arrow-right-s-line text-lg text-[#0F172A]"></i>
          </button>

          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto pb-4 px-6 scrollbar-hide scroll-smooth"
          >
            <div className="flex gap-4" style={{ width: 'max-content' }}>
              {locations.map((loc, index) => (
                <div 
                  key={loc.key} 
                  className={`w-72 flex-shrink-0 bg-white rounded-xl overflow-hidden shadow-lg transition-all duration-500 ${
                    visibleCards.includes(index) 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-6'
                  }`}
                >
                  <div className="relative h-36 overflow-hidden">
                    <img 
                      src={loc.image} 
                      alt={t(`serviceAreas.loc_${loc.key}_name`)}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    <div className="absolute inset-0 shadow-[inset_0_2px_15px_rgba(0,0,0,0.15)] pointer-events-none"></div>
                    <div className="absolute top-3 left-3">
                      <div className="w-8 h-8 rounded-lg bg-white/90 flex items-center justify-center">
                        <i className={`${loc.icon} text-sm text-accent`}></i>
                      </div>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-base font-bold text-white drop-shadow-lg">{t(`serviceAreas.loc_${loc.key}_name`)}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                      <span className="text-xs text-emerald-600 font-medium">{t('serviceAreas.available')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Location Cards Grid with Postcard Images */}
        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {locations.map((loc, index) => (
            <div
              key={loc.key}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`group relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${
                hoveredIndex === index
                  ? 'shadow-2xl shadow-accent/20 -translate-y-3'
                  : 'shadow-lg hover:shadow-xl'
              }`}
              style={{ transitionDelay: isVisible ? `${loc.delay}ms` : '0ms' }}
            >
              {/* Postcard Image */}
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  src={loc.image}
                  alt={t(`serviceAreas.loc_${loc.key}_name`)}
                  className={`w-full h-full object-cover object-top transition-transform duration-700 ${
                    hoveredIndex === index ? 'scale-110' : 'scale-100'
                  }`}
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                {/* Subtle inner shadow for elegance */}
                <div className="absolute inset-0 shadow-[inset_0_2px_20px_rgba(0,0,0,0.12)] pointer-events-none"></div>
                
                {/* Location badge on image */}
                <div className="absolute top-4 left-4">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center backdrop-blur-sm transition-all duration-500 ${
                      hoveredIndex === index
                        ? 'bg-accent text-white'
                        : 'bg-white/90 text-accent'
                    }`}
                  >
                    <i className={`${loc.icon} text-lg`}></i>
                  </div>
                </div>

                {/* Location name on image */}
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-bold text-white drop-shadow-lg">
                    {t(`serviceAreas.loc_${loc.key}_name`)}
                  </h3>
                </div>

                {/* Postcard stamp effect */}
                <div className={`absolute top-4 right-4 transition-all duration-500 ${
                  hoveredIndex === index ? 'opacity-100 rotate-0' : 'opacity-0 rotate-12'
                }`}>
                  <div className="w-12 h-12 rounded-lg border-2 border-dashed border-white/60 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                    <i className="ri-map-pin-2-fill text-white text-lg"></i>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-5">
                {/* Service Available indicator */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                    <span className="text-xs text-emerald-600 font-medium">{t('serviceAreas.available')}</span>
                  </div>
                  
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                      hoveredIndex === index
                        ? 'bg-accent text-white translate-x-0'
                        : 'bg-gray-100 text-gray-400 -translate-x-2'
                    }`}
                  >
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </div>
                </div>
              </div>

              {/* Accent border on hover */}
              <div
                className={`absolute bottom-0 left-0 right-0 h-1 transition-all duration-500 ${
                  hoveredIndex === index ? 'bg-accent' : 'bg-transparent'
                }`}
              ></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`relative rounded-2xl md:rounded-3xl overflow-hidden mt-8 md:mt-0 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Beautiful%20panoramic%20aerial%20view%20of%20Los%20Angeles%20California%20skyline%20at%20golden%20hour%20sunset%2C%20palm%20trees%20silhouettes%2C%20modern%20city%20buildings%2C%20warm%20orange%20and%20teal%20tones%2C%20cinematic%20wide%20angle%20photography%2C%20stunning%20urban%20landscape%20with%20mountains%20in%20background&width=1400&height=500&seq=lacta-elegant-2025&orientation=landscape"
              alt="Los Angeles skyline"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/80 to-[#0F172A]/60"></div>
          </div>

          <div className="relative px-6 py-10 md:px-14 md:py-16 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
            <div className="max-w-lg text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-3 md:mb-4">
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <i className="ri-map-pin-line text-accent text-xs md:text-sm"></i>
                </div>
                <span className="text-white/60 text-xs md:text-sm font-medium">{t('serviceAreas.ctaTag')}</span>
              </div>
              <h3 className="text-xl md:text-3xl font-bold text-white mb-2 md:mb-3 leading-tight">
                {t('serviceAreas.ctaTitle')}
              </h3>
              <p className="text-white/60 text-xs md:text-base leading-relaxed">
                {t('serviceAreas.ctaSubtitle')}
              </p>
            </div>

            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="group inline-flex items-center gap-2 md:gap-3 bg-white hover:bg-gray-50 text-[#0F172A] px-6 py-3 md:px-8 md:py-4 rounded-full font-semibold text-sm md:text-base transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              {t('serviceAreas.ctaButton')}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
